import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo, useRef, useState } from "react";
import PaymentMethodApi from "../../api/subscription/PaymentMethodApi";
import SubscriptionAPI from "../../api/subscription/SubscriptionApi";
import { EntitlementAction } from "../../api/entitlementsApi";
import {
  NotificationVariant,
  useNotifications,
} from "../../components/dashboard/NotificationsProvider";
import useEntitlements from "../../hooks/useEntitlements";
import useProfileModal from "../../hooks/useProfileModal";
import useRoles from "../../hooks/useRoles";
import { CaptureSession } from "../../types/payment-method";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import { getBillingOrgAuthId } from "../../utils/getBillingOrgAuthId";
import { hasSubscriptionManagerRole } from "../../utils/roleUtils";
import { useCurrentOrganization } from "../facility/hooks/useOrganization";
import AddPaymentMethodModal from "./AddPaymentMethodModal";
import TermsAcceptanceModal from "./TermsAcceptanceModal";
import { useTermsAcceptance } from "./hooks/useTermsAcceptance";
import { invalidateBillingQueries } from "./invalidateBillingQueries";
import {
  isUsableCaptureSession,
  MISSING_CAPTURE_SESSION_MESSAGE,
} from "./utils/isUsableCaptureSession";
import {
  formatPaymentMethodPromptMessage,
  getPaymentMethodPromptDismissedKey,
  getPaymentMethodPromptReasons,
  isNotFoundError,
  NON_SUBSCRIPTION_MANAGER_BILLING_MESSAGE,
} from "./utils/shouldPromptForPaymentMethod";
import { getTermsAcceptanceRequiredMessage } from "./utils/termsAcceptanceRequired";

function readDismissed(orgId?: string) {
  if (!orgId) {
    return false;
  }
  try {
    return (
      sessionStorage.getItem(getPaymentMethodPromptDismissedKey(orgId)) ===
      "true"
    );
  } catch {
    return false;
  }
}

function writeDismissed(orgId: string, dismissed: boolean) {
  try {
    const key = getPaymentMethodPromptDismissedKey(orgId);
    if (dismissed) {
      sessionStorage.setItem(key, "true");
    } else {
      sessionStorage.removeItem(key);
    }
  } catch {
    // Ignore sessionStorage failures (private mode, disabled storage).
  }
}

function roleNames(roles: unknown): string[] {
  if (!Array.isArray(roles)) {
    return [];
  }
  return roles.map((role) =>
    typeof role === "string" ? role : role?.name || "",
  );
}

/**
 * Prompts Subscription Managers to add a payment method when none is on file.
 * A due subscription or an open invoice is included in the message, and does
 * not open the prompt when a payment method is already saved.
 * Dismissing keeps a banner that opens the add-payment-method modal.
 * Other roles see a notice to contact their Subscription Manager.
 * Both stay hidden unless the allowPaymentPrompt flag is on.
 */
const PaymentMethodPromptGate = () => {
  const { allowPaymentPrompt } = useFlags();
  const roles = useRoles();
  const organization = useCurrentOrganization();
  const orgId = getBillingOrgAuthId(organization);
  const { isOpen: isProfileModalOpen } = useProfileModal();
  const {
    can,
    isOnboarded,
    isLoading: entitlementsLoading,
  } = useEntitlements();
  const { setNotification } = useNotifications();
  const queryClient = useQueryClient();
  const knownRoles = roleNames(roles);
  const rolesKnown = Array.isArray(roles);
  const isSubscriptionManager = hasSubscriptionManagerRole(knownRoles);
  const canManageSubscription =
    isSubscriptionManager && can(EntitlementAction.SUBSCRIPTION_MANAGEMENT);
  const subscriptionOnboarded = isOnboarded("subscription");
  const shouldCheck =
    allowPaymentPrompt === true &&
    subscriptionOnboarded &&
    !!orgId &&
    !isProfileModalOpen;
  const shouldLoadManagerBilling = shouldCheck && canManageSubscription;

  const subscriptionApi = useMemo(() => new SubscriptionAPI(), []);
  const paymentMethodApi = useMemo(() => new PaymentMethodApi(), []);

  const {
    featureEnabled,
    isLoading: termsLoading,
    needsAcceptance,
    termsModalOpen,
    setTermsModalOpen,
    promptTermsAcceptance,
    requireAcceptance,
    handleAccepted,
    currentVersion,
  } = useTermsAcceptance(shouldLoadManagerBilling ? orgId : undefined);

  const paymentMethodsQuery = useQuery({
    queryKey: ["paymentMethods", orgId],
    queryFn: () => subscriptionApi.getPaymentMethod(orgId as string),
    enabled: shouldCheck,
    staleTime: 30_000,
  });
  const subscriptionQuery = useQuery({
    queryKey: ["subscription", orgId],
    queryFn: () => subscriptionApi.getSubscription(orgId as string),
    enabled: shouldLoadManagerBilling,
    staleTime: 30_000,
  });
  const invoicesQuery = useQuery({
    queryKey: ["unpaidInvoices", orgId],
    queryFn: () => subscriptionApi.getInvoices(orgId as string),
    enabled: shouldLoadManagerBilling,
    staleTime: 30_000,
  });

  const paymentMethodsKnown =
    paymentMethodsQuery.isSuccess ||
    (paymentMethodsQuery.isError && isNotFoundError(paymentMethodsQuery.error));
  const subscriptionKnown =
    subscriptionQuery.isSuccess ||
    (subscriptionQuery.isError && isNotFoundError(subscriptionQuery.error));
  // isInitialLoading is only the first fetch. A background refetch keeps the
  // last result so the dialog and banner do not blank while it is in flight.
  const billingLoaded =
    !paymentMethodsQuery.isInitialLoading &&
    !subscriptionQuery.isInitialLoading &&
    !invoicesQuery.isInitialLoading &&
    paymentMethodsKnown &&
    subscriptionKnown;

  const reasons = useMemo(
    () =>
      billingLoaded
        ? getPaymentMethodPromptReasons({
            paymentMethods: paymentMethodsQuery.data ?? [],
            paymentMethodsKnown,
            subscriptionStatus: subscriptionQuery.data?.status,
            invoices: invoicesQuery.isSuccess ? invoicesQuery.data : [],
          })
        : [],
    [
      billingLoaded,
      invoicesQuery.data,
      invoicesQuery.isSuccess,
      paymentMethodsKnown,
      paymentMethodsQuery.data,
      subscriptionQuery.data?.status,
    ],
  );

  const [promptOpen, setPromptOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => readDismissed(orgId));
  const [setupLoading, setSetupLoading] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [captureSession, setCaptureSession] = useState<CaptureSession | null>(
    null,
  );
  const didAutoPromptRef = useRef(false);
  const [savedAtPaymentMethodsUpdate, setSavedAtPaymentMethodsUpdate] =
    useState<number | null>(null);

  useEffect(() => {
    didAutoPromptRef.current = false;
    setDismissed(readDismissed(orgId));
    setPromptOpen(false);
    setSavedAtPaymentMethodsUpdate(null);
  }, [orgId]);

  const termsBlocking =
    featureEnabled && (termsLoading || needsAcceptance || termsModalOpen);
  const paymentMethodRefreshPending =
    savedAtPaymentMethodsUpdate != null &&
    paymentMethodsQuery.dataUpdatedAt <= savedAtPaymentMethodsUpdate;
  const readyToPrompt =
    shouldLoadManagerBilling &&
    billingLoaded &&
    !entitlementsLoading &&
    !termsBlocking &&
    !paymentMethodRefreshPending &&
    reasons.length > 0;
  const noPaymentMethodOnFile = paymentMethodsQuery.isSuccess
    ? (paymentMethodsQuery.data?.length ?? 0) === 0
    : paymentMethodsQuery.isError && isNotFoundError(paymentMethodsQuery.error);
  const showNonManagerNotice =
    shouldCheck &&
    rolesKnown &&
    !entitlementsLoading &&
    !isSubscriptionManager &&
    !paymentMethodsQuery.isInitialLoading &&
    paymentMethodsKnown &&
    noPaymentMethodOnFile;

  useEffect(() => {
    if (savedAtPaymentMethodsUpdate == null || paymentMethodsQuery.isFetching) {
      return;
    }
    if (
      paymentMethodsQuery.isError ||
      paymentMethodsQuery.dataUpdatedAt > savedAtPaymentMethodsUpdate
    ) {
      setSavedAtPaymentMethodsUpdate(null);
    }
  }, [
    paymentMethodsQuery.dataUpdatedAt,
    paymentMethodsQuery.isError,
    paymentMethodsQuery.isFetching,
    savedAtPaymentMethodsUpdate,
  ]);

  useEffect(() => {
    if (!readyToPrompt || dismissed || didAutoPromptRef.current) {
      return;
    }
    didAutoPromptRef.current = true;
    setPromptOpen(true);
  }, [dismissed, readyToPrompt]);

  useEffect(() => {
    if (!billingLoaded || paymentMethodRefreshPending || reasons.length > 0) {
      return;
    }
    setPromptOpen(false);
  }, [billingLoaded, paymentMethodRefreshPending, reasons.length]);

  if (showNonManagerNotice) {
    return (
      <Box sx={{ mb: 2 }} data-testid="billing-setup-attention-banner">
        <Alert severity="warning" sx={{ borderRadius: 2 }}>
          {NON_SUBSCRIPTION_MANAGER_BILLING_MESSAGE}
        </Alert>
      </Box>
    );
  }

  if (!shouldLoadManagerBilling) {
    return null;
  }

  const message = formatPaymentMethodPromptMessage(reasons);
  const showPrompt =
    promptOpen && !addModalOpen && !termsModalOpen && reasons.length > 0;

  const dismissPrompt = () => {
    if (orgId) {
      writeDismissed(orgId, true);
    }
    setDismissed(true);
    setPromptOpen(false);
  };

  const startPaymentSetup = async () => {
    const response = await paymentMethodApi.getSetupIntent(orgId as string);
    setCaptureSession(response);
    setClientSecret(response.clientSecret || "");
    if (isUsableCaptureSession(response)) {
      setPromptOpen(false);
      setAddModalOpen(true);
    } else {
      setNotification({
        message: MISSING_CAPTURE_SESSION_MESSAGE,
        variant: NotificationVariant.error,
      });
    }
  };

  const handleAddPaymentMethod = async () => {
    try {
      setSetupLoading(true);
      const accepted = await requireAcceptance();
      if (!accepted) {
        setPromptOpen(false);
        return;
      }
      await startPaymentSetup();
    } catch (error) {
      const termsMessage = getTermsAcceptanceRequiredMessage(error);
      if (termsMessage) {
        setNotification({
          message: termsMessage,
          variant: NotificationVariant.warning,
        });
        setPromptOpen(false);
        promptTermsAcceptance();
        return;
      }
      setNotification({
        message: getApiErrorMessage(
          error,
          "Unable to start adding a payment method. Please try again.",
        ),
        variant: NotificationVariant.error,
      });
    } finally {
      setSetupLoading(false);
    }
  };

  const handleTermsAccepted = async () => {
    await handleAccepted();
    try {
      setSetupLoading(true);
      await startPaymentSetup();
    } catch (error) {
      setNotification({
        message: getApiErrorMessage(
          error,
          "Unable to start adding a payment method. Please try again.",
        ),
        variant: NotificationVariant.error,
      });
    } finally {
      setSetupLoading(false);
    }
  };

  const handleSaved = () => {
    setAddModalOpen(false);
    setPromptOpen(false);
    didAutoPromptRef.current = false;
    setSavedAtPaymentMethodsUpdate(paymentMethodsQuery.dataUpdatedAt);
    if (orgId) {
      invalidateBillingQueries(queryClient, orgId).catch(() => undefined);
    }
  };

  return (
    <>
      {dismissed &&
      reasons.length > 0 &&
      !showPrompt &&
      !termsBlocking &&
      !addModalOpen &&
      !paymentMethodRefreshPending ? (
        <Box sx={{ mb: 2 }} data-testid="payment-method-prompt-banner">
          <Alert
            severity="warning"
            sx={{ borderRadius: 2 }}
            action={
              <Button
                color="inherit"
                size="small"
                onClick={handleAddPaymentMethod}
                disabled={setupLoading}
                data-testid="payment-method-prompt-banner-link"
                startIcon={
                  setupLoading ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : undefined
                }
              >
                Add a payment method
              </Button>
            }
          >
            {message}
          </Alert>
        </Box>
      ) : null}
      <Dialog
        open={showPrompt}
        onClose={dismissPrompt}
        TransitionProps={{ timeout: 0 }}
        data-testid="payment-method-prompt"
        aria-labelledby="payment-method-prompt-title"
      >
        <DialogTitle id="payment-method-prompt-title">
          Add a payment method
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={dismissPrompt}
            data-testid="payment-method-prompt-dismiss"
          >
            Not now
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPaymentMethod}
            disabled={setupLoading}
            data-testid="payment-method-prompt-confirm"
            startIcon={
              setupLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Add payment method
          </Button>
        </DialogActions>
      </Dialog>
      <AddPaymentMethodModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        orgId={orgId || ""}
        onSaved={handleSaved}
        clientSecret={clientSecret || ""}
        captureSession={captureSession}
      />
      <TermsAcceptanceModal
        open={termsModalOpen}
        onClose={() => setTermsModalOpen(false)}
        orgId={orgId || ""}
        terms={currentVersion}
        onAccepted={handleTermsAccepted}
      />
    </>
  );
};

export default PaymentMethodPromptGate;
